import { Dispatch, SetStateAction, useState, SyntheticEvent } from 'react'
import { useIntl } from 'react-intl'
import { mdiArrowDown, mdiArrowUp } from '@mdi/js'
import type { SortingRule } from 'react-table'

import { getSortLabel } from '../../utils'
import type { TableHeader } from '../../types'
import { sortPopupMessages } from './messages'
import { commonMessages } from 'app/i18n/messages'
import { Body16 } from 'components/Typography'
import {
  SortPopupApplyButton,
  SortPopupButtons,
  SortPopupCancelButton,
  SortPopupContainer,
  SortPopupIcon,
  SortPopupIconButton,
  SortPopupIcons,
  SortPopupLi,
  SortPopupTitle,
  SortPopupUl,
  SortPopupWrapper,
} from './styles'

interface SortPopupProps<T> {
  headers: Record<string, TableHeader>
  handleClose: () => void
  sortOptions: Array<SortingRule<T>>
  sortBy: SortingRule<T>
  setSortBy: Dispatch<SetStateAction<SortingRule<T>>>
}

function SortPopup<T>({
  headers,
  handleClose,
  sortOptions,
  sortBy,
  setSortBy,
}: SortPopupProps<T>) {
  const { formatMessage } = useIntl()
  const [selectedSortBy, setSelectedSortBy] = useState<SortingRule<T>>(sortBy)

  const handleApply = () => {
    setSortBy(selectedSortBy)
    handleClose()
  }

  const setSelectedSortByDesc = (
    event: SyntheticEvent,
    option: SortingRule<T>,
    desc: boolean
  ) => {
    event.preventDefault()
    event.stopPropagation()
    setSelectedSortBy({
      id: option.id,
      desc: desc,
    })
  }

  return (
    <SortPopupContainer>
      <SortPopupWrapper>
        <SortPopupTitle>
          {formatMessage(sortPopupMessages.title)}
        </SortPopupTitle>
        <SortPopupUl>
          {sortOptions.map((option) => (
            <SortPopupLi
              key={option.id}
              selected={selectedSortBy.id === option.id}
              onClick={() => setSelectedSortBy(option)}
            >
              <Body16>{getSortLabel(headers, option)}</Body16>
              {selectedSortBy.id === option.id && (
                <SortPopupIcons>
                  <SortPopupIconButton
                    onClick={(event) =>
                      setSelectedSortByDesc(event, option, false)
                    }
                  >
                    <SortPopupIcon
                      path={mdiArrowUp}
                      selected={!selectedSortBy.desc}
                    />
                  </SortPopupIconButton>
                  <SortPopupIconButton
                    onClick={(event) =>
                      setSelectedSortByDesc(event, option, true)
                    }
                  >
                    <SortPopupIcon
                      path={mdiArrowDown}
                      selected={!!selectedSortBy.desc}
                    />
                  </SortPopupIconButton>
                </SortPopupIcons>
              )}
            </SortPopupLi>
          ))}
        </SortPopupUl>
      </SortPopupWrapper>
      <SortPopupButtons>
        <SortPopupCancelButton variant="secondary" onClick={handleClose}>
          {formatMessage(commonMessages.cancel)}
        </SortPopupCancelButton>
        <SortPopupApplyButton variant="primary" onClick={handleApply}>
          {formatMessage(commonMessages.apply)}
        </SortPopupApplyButton>
      </SortPopupButtons>
    </SortPopupContainer>
  )
}

export default SortPopup
